import * as React from 'react';

import { Recommendation, RecommendationSkeleton } from '../../components/RedesignedRecommendation';
import { RecommendationsList } from '../../components/RedesignedRecommendationsList';
import { RedesignedPaginationError } from '../../components/RedesignedPaginationError';
import { EPaginationStatus } from '../../slices/recommendationsSlice';
import { FavoriteButtonContainer } from '../FavoriteButtonContainer';
import { PikPromoBannerContainer } from '../PikPromoBannerContainer';
import { useInfiniteRecommendations } from '../RecommendationsListContainer/useInfiniteRecommendations';
import type { useRecommendations } from '../RecommendationsListContainer/useRecommendations';

import * as styles from './RedesignedRecommendationsListMobile.css';

const PAGINATION_SKELETON_COUNT = 8;
const PIK_BANNER_POSITION = 20;

type TRedesignedRecommendationsListMobileProps = {
  recommendationsControls: ReturnType<typeof useRecommendations>;
  onTrack: (offerId: number | null, position: number, contextToken?: string) => void;
};

export const RedesignedRecommendationsListMobile: React.FC<TRedesignedRecommendationsListMobileProps> = ({
  recommendationsControls,
  onTrack,
}) => {
  const {
    onShow,
    recommendations,
    onRecommendationClick,
    onShowTrap,
    isOfferVisited,
    hasMore,
    paginationStatus,
    onLoadMore,
    onRetryLoadMore,
  } = recommendationsControls;

  const { sentinelRef } = useInfiniteRecommendations({
    onShow,
    recommendations,
    hasMore,
    paginationStatus,
    onLoadMore,
  });

  const paginationSkeletons = React.useMemo(
    () =>
      new Array(PAGINATION_SKELETON_COUNT)
        .fill(0)
        .map((_, index) => (
          <RecommendationSkeleton key={`redesigned-recommendation-mobile-pagination-skeleton-${index}`} />
        )),
    [],
  );

  return (
    <RecommendationsList onShow={onShow}>
      {recommendations.map((recommendation, index) => (
        <React.Fragment key={`redesigned-recommendation-${index}`}>
          <Recommendation
            onTrack={onTrack}
            position={index + 1}
            onClick={onRecommendationClick}
            onShowTrap={onShowTrap}
            favoriteButton={<FavoriteButtonContainer size="XS" id={recommendation.offerId} />}
            isVisited={isOfferVisited(recommendation.offerId)}
            {...recommendation}
          />
          {index === PIK_BANNER_POSITION - 1 && <PikPromoBannerContainer />}
        </React.Fragment>
      ))}
      {paginationStatus === EPaginationStatus.Loading && paginationSkeletons}
      {paginationStatus === EPaginationStatus.Error && <RedesignedPaginationError onRetry={onRetryLoadMore} />}
      {paginationStatus !== EPaginationStatus.Error && hasMore && (
        <div ref={sentinelRef} className={styles['pagination-sentinel']} />
      )}
    </RecommendationsList>
  );
};
